import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext.js';
import { userService } from '../../services/user.js';
import profileTemplate from './profile.html?raw';
import './profile.css';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string | null;
  department?: string;
  position?: string;
  schedulingLink?: string;
}

export function ProfilePage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const { hasRole } = useAuth();
  const canSchedule =
    hasRole('admin') || hasRole('manager') || hasRole('employee') || hasRole('hr');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ─── Helpers ───────────────────────────────────────────

  const showMessage = useCallback((type: 'error' | 'success', message: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLDivElement>(`#profile-${type}`);
    const msgEl = container.querySelector<HTMLSpanElement>(`#profile-${type}-message`);
    if (el && msgEl) {
      msgEl.textContent = message;
      el.style.display = 'block';
      if (type === 'success')
        setTimeout(() => {
          el.style.display = 'none';
        }, 4000);
    }
    const other = type === 'error' ? 'success' : 'error';
    const otherEl = container.querySelector<HTMLDivElement>(`#profile-${other}`);
    if (otherEl) otherEl.style.display = 'none';
  }, []);

  // ─── Data Loading ──────────────────────────────────────

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    const result = await userService.getProfile();
    if (result.success && result.data) {
      setProfile(result.data as unknown as UserProfile);
    } else {
      showMessage('error', result.error?.message ?? 'Failed to load profile');
    }
    setIsLoading(false);
  }, [showMessage]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // ─── Render Profile ────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const loading = container.querySelector<HTMLDivElement>('#profile-loading');
    const content = container.querySelector<HTMLDivElement>('#profile-content');

    if (isLoading) {
      if (loading) loading.style.display = '';
      if (content) content.style.display = 'none';
      return;
    }
    if (loading) loading.style.display = 'none';
    if (!profile) return;
    if (content) content.style.display = '';

    // User info
    const nameEl = container.querySelector<HTMLSpanElement>('#profile-name');
    const emailEl = container.querySelector<HTMLSpanElement>('#profile-email');
    const roleEl = container.querySelector<HTMLSpanElement>('#profile-role');
    const deptEl = container.querySelector<HTMLSpanElement>('#profile-department');
    const posEl = container.querySelector<HTMLSpanElement>('#profile-position');

    if (nameEl) nameEl.textContent = `${profile.firstName} ${profile.lastName}`;
    if (emailEl) emailEl.textContent = profile.email;
    if (roleEl)
      roleEl.textContent = profile.role
        ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
        : '—';
    if (deptEl) deptEl.textContent = profile.department ?? '—';
    if (posEl) posEl.textContent = profile.position ?? '—';

    // Hide scheduling card for MC-only users
    const schedulingCard = container.querySelector<HTMLDivElement>('#scheduling-card');
    if (schedulingCard) schedulingCard.style.display = canSchedule ? '' : 'none';

    // Scheduling link
    const linkDisplay = container.querySelector<HTMLDivElement>('#scheduling-link-display');
    const linkForm = container.querySelector<HTMLDivElement>('#scheduling-link-form');
    const linkValue = container.querySelector<HTMLAnchorElement>('#scheduling-link-value');
    const linkInput = container.querySelector<HTMLInputElement>('#scheduling-link-input');

    if (profile.schedulingLink) {
      if (linkDisplay) linkDisplay.style.display = '';
      if (linkForm) linkForm.style.display = 'none';
      if (linkValue) {
        linkValue.textContent = profile.schedulingLink;
        linkValue.href = profile.schedulingLink;
      }
    } else {
      if (linkDisplay) linkDisplay.style.display = 'none';
      if (linkForm) linkForm.style.display = '';
      if (linkInput) linkInput.value = '';
    }
  }, [profile, isLoading, canSchedule]);

  // ─── Event Listeners ──────────────────────────────────

  const handleSaveLink = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;
    const input = container.querySelector<HTMLInputElement>('#scheduling-link-input');
    const value = input?.value?.trim();
    if (!value) {
      showMessage('error', 'Please enter a scheduling link URL');
      return;
    }
    const saveBtn = container.querySelector<HTMLButtonElement>('#save-link-btn');
    if (saveBtn) saveBtn.disabled = true;
    const result = await userService.updateSchedulingLink({ schedulingLink: value });
    if (result.success && result.data) {
      setProfile(result.data as unknown as UserProfile);
      showMessage('success', 'Scheduling link saved');
    } else {
      showMessage('error', result.error?.message ?? 'Failed to save scheduling link');
    }
    if (saveBtn) saveBtn.disabled = false;
  }, [showMessage]);

  const handleRemoveLink = useCallback(async () => {
    const removeBtn = containerRef.current?.querySelector<HTMLButtonElement>('#remove-link-btn');
    if (removeBtn) removeBtn.disabled = true;
    const result = await userService.removeSchedulingLink();
    if (result.success && result.data) {
      setProfile(result.data as unknown as UserProfile);
      showMessage('success', 'Scheduling link removed');
    } else {
      showMessage('error', result.error?.message ?? 'Failed to remove scheduling link');
    }
    if (removeBtn) removeBtn.disabled = false;
  }, [showMessage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const saveBtn = container.querySelector<HTMLButtonElement>('#save-link-btn');
    const removeBtn = container.querySelector<HTMLButtonElement>('#remove-link-btn');

    const handleSave = (): void => {
      void handleSaveLink();
    };
    const handleRemove = (): void => {
      void handleRemoveLink();
    };

    saveBtn?.addEventListener('click', handleSave);
    removeBtn?.addEventListener('click', handleRemove);

    return () => {
      saveBtn?.removeEventListener('click', handleSave);
      removeBtn?.removeEventListener('click', handleRemove);
    };
  }, [handleSaveLink, handleRemoveLink]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: profileTemplate }} />;
}
